/**
 * Wattec Custom PC Builder — power estimator
 *
 * Estimates system power draw from the CPU and GPU actually selected, and
 * checks it against the wattage of the PSU actually selected. Aris remains
 * the only thing that decides the price and the cart; this file only reads
 * the labels of the current CPU/GPU/PSU selections and does arithmetic on
 * numbers sourced from real manufacturer figures — nothing here is a guess
 * or an invented figure.
 *
 * Sourcing, in order:
 *   1. A wattage written into the component's own name or Aris description —
 *      "TDP 120W" is read first if present, otherwise a bare "120W" standing
 *      on its own. This is the primary path, and it is what makes the system
 *      future-proof: add a new CPU or GPU in Aris with its TDP written into
 *      either field, in either form, and it is picked up automatically —
 *      nothing in this file needs to change or be told about it.
 *   2. For the handful of GPUs already in the catalogue that do not carry a
 *      wattage yet, a small table of published reference-board TGP/TBP
 *      figures below, used only until Wattec adds the real figure to the
 *      listing — at which point step 1 takes over for that part and this
 *      table is no longer consulted for it.
 *   PSU wattage is always read from the PSU's own product name.
 *
 * If a component cannot be confidently matched to one of these — a brand new
 * SKU with no wattage written anywhere, for instance — no number is shown
 * for that build. A wrong number is worse than no number here.
 */
(function () {
  'use strict';

  // CPU + GPU, plus a flat 100W for everything else in the machine
  // (board, drives, fans, pump). Wattec judge a supply against that total.
  var CUSHION_WATTS = 100;

  /**
   * Every wattage figure found in a piece of text, filtered to a plausible
   * range for the kind of component being read. A number written as
   * "TDP 120W" counts the same as a bare "120W" — both are recognised.
   */
  function wattsIn(text, min, max) {
    var found = [];
    var re = /(\d+(?:\.\d+)?)\s*W\b/gi;
    var match;
    while ((match = re.exec(text))) {
      var n = parseFloat(match[1]);
      if (n >= min && n <= max) found.push(n);
    }
    return found;
  }

  /**
   * Reads a wattage out of a component's name and description together. An
   * explicit "TDP" figure is trusted over any other bare wattage mentioned
   * nearby — a spec sometimes lists more than one number, and only the one
   * labelled TDP is reliably the power draw. Where a component states both a
   * base and a turbo figure with no TDP label at all (a common CPU pattern,
   * e.g. "65W (121W Turbo)"), the higher of the two is used, since that is
   * the realistic figure under sustained load.
   */
  function listedWatts(label, spec, min, max) {
    var text = String(label || '') + ' ' + String(spec || '');

    var tdpValues = [];
    var tdpRe = /TDP\s*[:=]?\s*(\d+(?:\.\d+)?)\s*W\b/gi;
    var tdpMatch;
    while ((tdpMatch = tdpRe.exec(text))) {
      var n = parseFloat(tdpMatch[1]);
      if (n >= min && n <= max) tdpValues.push(n);
    }
    if (tdpValues.length) return Math.max.apply(null, tdpValues);

    var bare = wattsIn(text, min, max);
    return bare.length ? Math.max.apply(null, bare) : null;
  }

  /* ------------------------------------------------------------------
   * GPU reference power (official reference/Founders Edition TGP/TBP) —
   * fallback only, for parts not yet carrying their own wattage in Aris.
   * ------------------------------------------------------------------ */
  var GPU_FALLBACK = [
    [/RTX\s*5090/i, 575],
    [/RTX\s*5080/i, 360],
    [/RTX\s*5070\s*Ti/i, 300],
    [/RTX\s*5070/i, 250],
    [/RTX\s*5060\s*Ti/i, 180],
    [/RTX\s*5060/i, 145],
    [/RTX\s*5050/i, 130],
    [/RTX\s*4090/i, 450],
    [/RTX\s*4080/i, 320],
    [/RTX\s*4070\s*Ti/i, 285],
    [/RTX\s*4070/i, 200],
    [/RTX\s*4060\s*Ti/i, 160],
    [/RTX\s*4060/i, 115],
    // The RTX 3050 has two very different desktop cards: the original 8GB
    // board (130W) and a cut-down 6GB refresh (70W) — check memory size first.
    [/RTX\s*3050[\s\S]*6\s*GB/i, 70],
    [/RTX\s*3050/i, 130],
    [/RX\s*9070\s*XT/i, 304],
    [/RX\s*9070/i, 220],
    [/RX\s*9060\s*XT/i, 160],
    [/RX\s*7900\s*XTX/i, 355],
    [/RX\s*7900\s*XT/i, 315],
    [/RX\s*7800\s*XT/i, 263],
    [/RX\s*7700\s*XT/i, 245],
    [/RX\s*7600\s*XT/i, 190],
    [/RX\s*7600/i, 165],
    [/Arc\s*A770/i, 225],
    [/Arc\s*A750/i, 225],
    [/Arc\s*B580/i, 190],
    [/Arc\s*B570/i, 150]
  ];

  /* A part whose own name and description carry no wattage at all yet.
     Real, published AMD TDP — not an estimate — fallback only. */
  var CPU_FALLBACK = {
    'AMD Ryzen 9 5900x': 105
  };

  function gpuWatts(label, spec) {
    var listed = listedWatts(label, spec, 30, 700);
    if (listed != null) return { watts: listed, source: 'listed' };
    var text = String(label || '') + ' ' + String(spec || '');
    for (var i = 0; i < GPU_FALLBACK.length; i++) {
      if (GPU_FALLBACK[i][0].test(text)) return { watts: GPU_FALLBACK[i][1], source: 'reference' };
    }
    return null;
  }

  function cpuWatts(label, spec) {
    var listed = listedWatts(label, spec, 15, 400);
    if (listed != null) return { watts: listed, source: 'listed' };
    if (CPU_FALLBACK[label] != null) return { watts: CPU_FALLBACK[label], source: 'reference' };
    return null;
  }

  function psuWatts(label) {
    var direct = String(label || '').match(/(\d{3,4})\s*W\b/i);
    if (direct) return parseInt(direct[1], 10);
    // A handful of PSUs carry their wattage only inside a model code, e.g.
    // "RM750e" or "A1000G", with no "W" suffix at all.
    var runs = (String(label || '').match(/\d{3,4}/g) || [])
      .map(function (n) { return parseInt(n, 10); })
      .filter(function (n) { return n >= 300 && n <= 1600; });
    var unique = runs.filter(function (n, i) { return runs.indexOf(n) === i; });
    return unique.length === 1 ? unique[0] : null;
  }

  /**
   * Reads the current CPU, GPU and PSU choices from the adapter and returns
   * an estimate — or explains, per component, why it could not be produced.
   *
   * A customer who picks "Integrated Graphics" has made a real, complete
   * choice about their graphics, not a missing one — the CPU is doing the
   * work with no separate card to add watts for. So the estimate is ready as
   * soon as that choice is on-screen, the same as it would be for a chosen
   * dedicated card, rather than waiting on a GPU pick that was never coming.
   */
  function estimate(adapter) {
    var selection = adapter.getSelection().filter(function (item) { return item.visible; });
    var cpu = selection.filter(function (item) { return item.section === 'cpu'; })[0];
    var gpuChoice = selection.filter(function (item) {
      return item.section === 'gpu' && /graphic.*card.*option/i.test(item.optionLabel);
    })[0];
    var gpu = selection.filter(function (item) {
      return item.section === 'gpu' && !/graphic.*card.*option/i.test(item.optionLabel);
    })[0];
    var psu = selection.filter(function (item) { return item.section === 'psu'; })[0];

    var integrated = !!(gpuChoice && /integrated/i.test(gpuChoice.label));

    var cw = cpu ? cpuWatts(cpu.label, cpu.spec) : null;
    var gw = integrated ? { watts: 0, source: 'integrated' } : (gpu ? gpuWatts(gpu.label, gpu.spec) : null);
    var pw = psu ? psuWatts(psu.label) : null;

    var ready = cw != null && gw != null;
    var systemWatts = ready ? Math.round(cw.watts + gw.watts + CUSHION_WATTS) : null;

    var result = {
      cpu: cpu ? { label: cpu.label, watts: cw ? cw.watts : null, source: cw ? cw.source : null } : null,
      gpu: integrated
        ? { label: 'Integrated graphics', watts: 0, source: 'integrated' }
        : (gpu ? { label: gpu.label, watts: gw ? gw.watts : null, source: gw ? gw.source : null } : null),
      psu: psu ? { label: psu.label, watts: pw } : null,
      cushion: CUSHION_WATTS,
      systemWatts: systemWatts,
      ready: ready,
      percent: null,
      level: 'unknown'
    };

    if (systemWatts != null && pw) {
      result.percent = Math.round((systemWatts / pw) * 1000) / 10;
      // Green to 75% of the supply, amber to 90%, red above — Wattec's
      // own thresholds for how hard they will let a PSU be loaded.
      result.level = result.percent > 90 ? 'high' : result.percent > 75 ? 'medium' : 'low';
    }

    return result;
  }

  window.WattecPCBuilder = window.WattecPCBuilder || {};
  window.WattecPCBuilder.power = { estimate: estimate, CUSHION_WATTS: CUSHION_WATTS };
})();
